"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";
import { useParams } from "react-router-dom";
import { useEcho } from "@laravel/echo-react";

import { useClassroomStore } from "../store/useClassroomStore";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

// Define the form schema for video URL
const videoFormSchema = (t: any) =>
  z.object({
    videoUrl: z
      .string()
      .min(1, { message: t("classroom:videoUrlRequired") })
      .regex(
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?/,
        { message: t("classroom:invalidYouTubeUrl") },
      ),
  });

type VideoFormValues = z.infer<ReturnType<typeof videoFormSchema>>;

interface Activity {
  id: string;
  name: string;
  description: string;
  icon: string;
  colors: {
    primary: string;
    secondary: string;
  };
}

interface ActivitiesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ActivitiesDialog({ isOpen, onClose }: ActivitiesDialogProps) {
  const { t } = useTranslation(["classroom", "common"]);
  const { lessonId } = useParams();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setVideoSync, setView } = useClassroomStore();

  // Get whisper channel for sending events
  const { channel } = useEcho(
    `lesson.${lessonId}`,
    [], // No regular events, only whispers
    () => {},
    [lessonId],
  );

  // Available activities
  const activities: Activity[] = [
    {
      id: "watch-together",
      name: t("classroom:watchTogether"),
      description: t("classroom:watchTogetherDescription"),
      icon: "lucide:play",
      colors: {
        primary: "bg-red-500",
        secondary: "bg-white",
      },
    },
  ];

  // Initialize the form for video URL
  const form = useForm<VideoFormValues>({
    resolver: zodResolver(videoFormSchema(t)),
    defaultValues: {
      videoUrl: "",
    },
  });

  // Extract YouTube video ID from URL
  const extractYouTubeVideoId = (url: string): string | null => {
    const regex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?/;
    const match = url.match(regex);

    return match ? match[1] : null;
  };

  // Handle activity selection and go directly to form
  const handleActivitySelect = (activity: Activity) => {
    setSelectedActivity(activity);
    setStep(2);
  };

  // Handle form submission
  const onSubmit = async (data: VideoFormValues) => {
    if (!selectedActivity) return;

    try {
      setIsSubmitting(true);

      const videoId = extractYouTubeVideoId(data.videoUrl);

      if (!videoId) {
        form.setError("videoUrl", { message: t("classroom:invalidYouTubeUrl") });

        return;
      }

      const newVideoSync = {
        videoId,
        time: 0,
        isPlaying: false,
        lastUpdate: Date.now(),
      };

      // Set video sync data to start the activity
      setVideoSync(newVideoSync);

      // Switch tutor to video view after starting activity
      setView("video");

      // Send video set whisper to all participants so everyone switches to video view
      if (channel()) {
        channel().whisper("videoSet", {
          videoId,
          time: 0,
          isPlaying: false,
          senderId: "tutor", // Identifier for the sender
          blockedIds: [],
        });
      }

      onClose();
    } catch (error) {
      console.error("Failed to start activity:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close and reset
  const handleClose = () => {
    // If there's an active video activity, remove it when closing the dialog
    const { videoSync } = useClassroomStore.getState();

    if (videoSync && channel()) {
      console.log("Removing video activity on dialog close");

      // Clear video sync locally
      setVideoSync(null);

      // Send video removed whisper to all participants
      channel().whisper("videoRemoved", {
        senderId: "tutor",
        timestamp: Date.now(),
      });
    }

    setStep(1);
    setSelectedActivity(null);
    form.reset();
    onClose();
  };

  // Handle back navigation
  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedActivity(null);
    }
  };

  // Get dialog title based on step
  const getDialogTitle = (): string => {
    switch (step) {
      case 1:
        return t("classroom:selectActivity");
      case 2:
        return t("classroom:activitySettings");
      default:
        return t("classroom:selectActivity");
    }

    return t("classroom:selectActivity");
  };

  // Get dialog icon based on step
  const getDialogIcon = () => {
    if (step === 1) {
      return "lucide:activity";
    }

    return selectedActivity?.icon || "lucide:activity";
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-side-accent/80 text-accent-foreground h-[70dvh] max-w-2xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl max-sm:h-[100dvh] max-sm:max-h-dvh max-sm:w-[100dvw] max-sm:max-w-none max-sm:rounded-none">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="from-accent-foreground/40 to-accent-foreground/60 absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br" />
          <div className="from-accent-foreground/30 to-accent-foreground/50 absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr" />
        </div>

        <div className="relative flex h-full flex-col">
          <DialogHeader className="flex-shrink-0 p-6 pb-0 max-sm:p-4 max-sm:pb-0">
            <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
              <div className="bg-muted/20 flex h-10 w-10 items-center justify-center rounded-xl backdrop-blur-sm">
                <Icon
                  className="text-accent-foreground"
                  height="1.25rem"
                  icon={getDialogIcon()}
                  width="1.25rem"
                />
              </div>
              {getDialogTitle()}
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6 max-sm:px-4">
            <div className="py-6 max-sm:py-4">
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.div
                    key="step1"
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    initial={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="space-y-4">
                      {activities.map((activity, index) => (
                        <motion.div
                          key={activity.id}
                          animate={{ opacity: 1, y: 0 }}
                          className="border-border/50 bg-side-accent/50 hover:bg-side-accent/70 relative cursor-pointer overflow-hidden rounded-xl border p-6 backdrop-blur-sm transition-all hover:shadow-lg"
                          initial={{ opacity: 0, y: 10 }}
                          transition={{ delay: index * 0.1 }}
                          onClick={() => handleActivitySelect(activity)}
                        >
                          {/* Activity-specific background circles */}
                          <div className="absolute inset-0 opacity-10">
                            <div
                              className={`absolute -top-4 -right-4 h-16 w-16 rounded-full ${activity.colors.primary}`}
                            />
                            <div
                              className={`absolute -bottom-4 -left-4 h-12 w-12 rounded-full ${activity.colors.secondary}`}
                            />
                            <div
                              className={`absolute top-1/2 left-1/4 h-8 w-8 rounded-full ${activity.colors.primary} opacity-50`}
                            />
                          </div>

                          <div className="relative flex items-start gap-4">
                            <div
                              className={`flex flex-shrink-0 items-center justify-center rounded-xl p-2 ${activity.colors.primary}/20 backdrop-blur-sm`}
                            >
                              <Icon
                                className={`text-accent-foreground`}
                                height="1.5rem"
                                icon={activity.icon}
                                width="1.5rem"
                              />
                            </div>

                            <div className="min-w-0 flex-1">
                              <h3 className="text-accent-foreground mb-3 text-lg font-semibold">
                                {activity.name}
                              </h3>
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                {activity.description}
                              </p>
                            </div>

                            <Icon
                              className="text-muted-foreground flex-shrink-0"
                              height="1.25rem"
                              icon="lucide:chevron-right"
                              width="1.25rem"
                            />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="step2"
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    initial={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {selectedActivity && (
                      <div className="space-y-6">
                        {/* Selected activity header */}
                        <div className="border-border/50 bg-side-accent/50 relative overflow-hidden rounded-xl border p-4 backdrop-blur-sm">
                          {/* Activity-specific background circles */}
                          <div className="absolute inset-0 opacity-10">
                            <div
                              className={`absolute -top-2 -right-2 h-12 w-12 rounded-full ${selectedActivity.colors.primary}`}
                            />
                            <div
                              className={`absolute -bottom-2 -left-2 h-8 w-8 rounded-full ${selectedActivity.colors.secondary}`}
                            />
                          </div>

                          <div className="relative flex items-center gap-3">
                            <div
                              className={`flex items-center justify-center rounded-lg p-2 ${selectedActivity.colors.primary}/20`}
                            >
                              <Icon
                                className="text-accent-foreground"
                                height="1.25rem"
                                icon={selectedActivity.icon}
                                width="1.25rem"
                              />
                            </div>
                            <div>
                              <h3 className="text-accent-foreground font-semibold">
                                {selectedActivity.name}
                              </h3>
                              <p className="text-muted-foreground text-sm">
                                {selectedActivity.description}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Settings Form */}
                        <Form {...form}>
                          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                            <motion.div
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-side-accent/50 border-border/50 rounded-xl border p-5 backdrop-blur-sm"
                              initial={{ opacity: 0, y: 10 }}
                              transition={{ delay: 0.3 }}
                            >
                              <div className="mb-4 flex items-center gap-3">
                                <div className="bg-accent-foreground/10 flex items-center justify-center rounded-lg p-2">
                                  <Icon
                                    className="text-accent-foreground"
                                    height="1rem"
                                    icon="lucide:link"
                                    width="1rem"
                                  />
                                </div>
                                <h3 className="text-accent-foreground text-sm font-semibold">
                                  {t("classroom:videoUrl")}
                                </h3>
                              </div>

                              <FormField
                                control={form.control}
                                name="videoUrl"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-accent-foreground">
                                      {t("classroom:videoUrl")}
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        className="bg-background/50 border-border/50 text-accent-foreground placeholder:text-muted-foreground"
                                        placeholder={t("classroom:videoUrlPlaceholder")}
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </motion.div>
                          </form>
                        </Form>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ScrollArea>

          <DialogFooter className="flex flex-shrink-0 gap-3 p-6 pt-0 max-sm:p-4 max-sm:pt-0">
            {step > 1 && (
              <Button
                className="bg-muted/50 hover:bg-muted/30 border-border/50 rounded-xl"
                disabled={isSubmitting}
                type="button"
                variant="outline"
                onClick={handleBack}
              >
                <Icon className="mr-2" height="1rem" icon="lucide:arrow-left" width="1rem" />
                {t("classroom:back")}
              </Button>
            )}

            <Button
              className="bg-muted/50 hover:bg-muted/30 border-border/50 rounded-xl"
              disabled={isSubmitting}
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              {t("common:cancel")}
            </Button>

            {step === 2 && (
              <Button
                className="bg-accent-foreground text-primary hover:bg-accent-foreground/80 rounded-xl"
                disabled={isSubmitting || !selectedActivity}
                type="submit"
                onClick={form.handleSubmit(onSubmit)}
              >
                {isSubmitting ? (
                  <>
                    <Icon
                      className="mr-2 animate-spin"
                      height="1rem"
                      icon="lucide:loader-2"
                      width="1rem"
                    />
                    {t("common:processing")}
                  </>
                ) : (
                  <>
                    <Icon className="mr-2" height="1rem" icon="lucide:play" width="1rem" />
                    {t("classroom:startActivity")}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
